<script setup lang="ts">
  import { computed, ref } from "vue";
  import { Check, X } from "lucide-vue-next";
  import {
    M_CAMPAIGN_PUSH_STAGE_LABELS,
    M_CAMPAIGN_PUSH_STAGE_SINGULAR_LABELS,
    pushFailureAttemptsLabel,
  } from "@/components/MCampaign/Pushes/mCampaignPushStatus";
  import type { MCampaignPushStage } from "@/types";

  interface StageProgress {
    stage: MCampaignPushStage;
    total: number;
    syncBefore: number;
    syncNew: number;
    errors: number;
    retrying?: number;
    retryPass?: number;
  }

  interface ToastedFailure {
    stage: MCampaignPushStage;
    name: string | null;
    message: string;
    attempts: number;
  }

  interface PushTask {
    taskInfo?: { mCampaignName?: string; mCampaignToken?: string };
    details?: {
      currentStage?: MCampaignPushStage | "finalizing";
      retryPasses?: number;
      stages?: StageProgress[];
      failures?: ToastedFailure[];
      failuresCount?: number;
    } | null;
  }

  const props = defineProps<{
    status: "loading" | "success" | "error";
    task: PushTask;
    onClose?: () => void;
  }>();

  const STAGES = Object.keys(M_CAMPAIGN_PUSH_STAGE_LABELS) as MCampaignPushStage[];

  const errorsOpen = ref(false);

  const campaignName = computed(() => props.task.taskInfo?.mCampaignName ?? "campaign");
  const running = computed(() => props.status === "loading");
  const failures = computed(() => props.task.details?.failures ?? []);
  const failuresCount = computed(() => props.task.details?.failuresCount ?? failures.value.length);
  const retryPasses = computed(() => props.task.details?.retryPasses);
  const currentIndex = computed(() => {
    const currentStage = props.task.details?.currentStage ?? "campaigns";
    return currentStage === "finalizing" ? STAGES.length : STAGES.indexOf(currentStage);
  });

  const pluralize = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;
  const errorsLabel = (count: number) => pluralize(count, "error");

  const stageProgress = computed(() =>
    STAGES.map((stage, index) => {
      const progress = props.task.details?.stages?.find((item) => item.stage === stage);
      const toPush = Math.max(0, (progress?.total ?? 0) - (progress?.syncBefore ?? 0));
      const pushed = progress?.syncNew ?? 0;
      const errors = progress?.errors ?? 0;

      return {
        stage,
        index,
        label: M_CAMPAIGN_PUSH_STAGE_LABELS[stage],
        toPush,
        pushed,
        errors,
        count: `${pushed + errors}/${toPush}`,
        okWidth: toPush ? `${(pushed / toPush) * 100}%` : "0%",
        errorWidth: toPush ? `${(errors / toPush) * 100}%` : "0%",
        retrying: running.value ? progress?.retrying ?? 0 : 0,
        retryPass: progress?.retryPass ?? 0,
      };
    }).filter((step) => step.toPush > 0),
  );

  const activeIndex = computed(() => {
    if (!running.value || !props.task.details) return -1;
    return stageProgress.value.find((step) => step.index >= currentIndex.value)?.index ?? -1;
  });

  const steps = computed(() =>
    stageProgress.value.map((step) => {
      let state: "done" | "active" | "stopped" | "pending" = "pending";
      if (props.status === "success" || step.index < currentIndex.value) state = "done";
      else if (step.index === activeIndex.value) state = "active";
      else if (!running.value && step.index === currentIndex.value) state = "stopped";
      return { ...step, state };
    }),
  );

  const title = computed(() => {
    if (running.value) return `Pushing ${campaignName.value}`;
    if (props.status === "success") return `${campaignName.value} pushed`;
    if (failuresCount.value === 0) return `${campaignName.value} push stopped`;
    return `${campaignName.value} pushed with ${errorsLabel(failuresCount.value)}`;
  });

  const subtitle = computed(() => {
    if (running.value) {
      if (!props.task.details) return "Preparing the push";
      const position = steps.value.findIndex((step) => step.state === "active");
      if (position === -1) return "Nothing left to push";
      return `Step ${position + 1} of ${steps.value.length} · ${steps.value[position].label}`;
    }
    if (props.status === "error") {
      return failuresCount.value === 0 ? "An unexpected error stopped the push" : "Failed items were skipped";
    }

    const pushedSummary = steps.value
      .filter((step) => step.pushed > 0)
      .map((step) => pluralize(step.pushed, M_CAMPAIGN_PUSH_STAGE_SINGULAR_LABELS[step.stage].toLowerCase()))
      .join(" · ");
    return pushedSummary || "Everything was already in sync";
  });

  const hiddenFailuresCount = computed(() => Math.max(0, failuresCount.value - failures.value.length));
</script>

<template>
  <div
    class="flex w-[400px] max-w-[calc(100vw-40px)] flex-col gap-3.5 rounded-xl border bg-white p-4 font-sans shadow-lg"
    :role="status === 'error' ? 'alert' : 'status'"
    :aria-live="status === 'error' ? 'assertive' : 'polite'"
    data-cy="m-campaign-push-toast">
    <div class="flex items-start gap-2.5">
      <div
        class="flex size-7 shrink-0 items-center justify-center rounded-full"
        :class="{
          'bg-muted text-episto-navy': running,
          'bg-status-success-soft text-status-success': status === 'success',
          'bg-status-error-soft text-status-error': status === 'error',
        }">
        <span
          v-if="running"
          class="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" />
        <Check
          v-else-if="status === 'success'"
          :size="16"
          :stroke-width="2.5" />
        <svg
          v-else
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round">
          <path d="M12 8v5M12 16.5v.01" />
        </svg>
      </div>
      <div class="flex min-w-0 flex-1 flex-col gap-0.5">
        <p class="m-0 text-sm leading-5 font-semibold break-words text-episto-navy">{{ title }}</p>
        <p class="m-0 text-xs text-muted-foreground">{{ subtitle }}</p>
      </div>
      <button
        type="button"
        class="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-subtle hover:bg-muted hover:text-foreground"
        :aria-label="running ? 'Hide' : 'Close'"
        @click="onClose?.()">
        <X
          :size="14"
          :stroke-width="2.4" />
      </button>
    </div>

    <div
      v-if="steps.length"
      class="flex flex-col gap-2">
      <div
        v-for="step in steps"
        :key="step.stage"
        class="grid grid-cols-[16px_minmax(0,1fr)_72px_52px] items-center gap-2.5 text-13">
        <div class="flex items-center justify-center">
          <Check
            v-if="step.state === 'done'"
            :size="14"
            :stroke-width="3"
            :class="step.errors ? 'text-status-error' : 'text-status-success'" />
          <X
            v-else-if="step.state === 'stopped'"
            :size="14"
            :stroke-width="3"
            class="text-status-error" />
          <span
            v-else-if="step.state === 'active'"
            class="size-2.5 animate-spin rounded-full border-2 border-episto-navy border-r-transparent" />
          <span
            v-else
            class="size-1.5 rounded-full bg-border-strong" />
        </div>
        <div
          class="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5"
          :class="[
            step.state === 'pending' ? 'text-foreground-subtle' : 'text-episto-navy',
            step.state === 'active' && 'font-semibold',
          ]">
          <span class="shrink-0">{{ step.label }}</span>
          <span
            v-if="step.retrying > 0"
            class="rounded-full bg-muted px-1.5 text-2xs font-medium whitespace-nowrap text-muted-foreground">
            Retrying {{ step.retrying }}<template v-if="retryPasses"> ({{ step.retryPass }}/{{ retryPasses }})</template>
          </span>
          <span
            v-if="step.errors > 0"
            class="rounded-full bg-status-error-soft px-1.5 text-2xs font-semibold whitespace-nowrap text-status-error-strong">
            {{ errorsLabel(step.errors) }}
          </span>
        </div>
        <div class="flex h-1 overflow-hidden rounded-full bg-muted">
          <div
            class="bg-status-success transition-[width] duration-300"
            :style="{ width: step.okWidth }" />
          <div
            class="bg-status-error transition-[width] duration-300"
            :style="{ width: step.errorWidth }" />
        </div>
        <div
          class="text-right tabular-nums"
          :class="step.state === 'pending' ? 'text-foreground-subtle' : 'text-episto-navy'">
          {{ step.count }}
        </div>
      </div>
    </div>

    <div
      v-if="failuresCount > 0"
      class="flex flex-col gap-2.5 border-t border-muted pt-3">
      <div class="flex items-center text-xs">
        <span class="flex-1 font-semibold text-status-error-strong">{{ errorsLabel(failuresCount) }}</span>
        <button
          type="button"
          class="cursor-pointer font-medium text-episto-navy hover:text-episto-teal-700"
          @click="errorsOpen = !errorsOpen">
          {{ errorsOpen ? "Hide errors" : "View errors" }}
        </button>
      </div>
      <div
        v-if="errorsOpen"
        class="flex max-h-48 flex-col gap-2 overflow-auto">
        <div
          v-for="(failure, index) in failures"
          :key="index"
          class="flex flex-col gap-px text-xs">
          <span class="font-medium text-episto-navy"
            >{{ M_CAMPAIGN_PUSH_STAGE_SINGULAR_LABELS[failure.stage] }} · {{ failure.name ?? "Unnamed" }}</span
          >
          <span class="break-words text-status-error-strong">{{ failure.message }}</span>
          <span class="text-foreground-subtle">{{ pushFailureAttemptsLabel(failure.attempts) }}</span>
        </div>
        <RouterLink
          v-if="hiddenFailuresCount > 0 && task.taskInfo?.mCampaignToken"
          :to="{ name: 'MCampaignPushes', params: { token: task.taskInfo.mCampaignToken } }"
          class="text-xs font-medium text-episto-navy hover:text-episto-teal-700">
          {{ hiddenFailuresCount }} more in the push history
        </RouterLink>
      </div>
    </div>
  </div>
</template>
